import { v7 as uuidv7 } from 'uuid'
import type { Form, FormInputComponent } from '../types'

export interface ExportedForm {
	version: string
	name: string
	components: FormInputComponent[]
	exportedAt: string
}

/**
 * Exports a form to a JSON string
 */
export const exportFormToJSON = (form: Form): string => {
	const exportData: ExportedForm = {
		version: '1.0',
		name: form.name,
		components: form.components.map((component) => ({
			...component,
			custom: component.custom as Record<string, unknown>,
		})),
		exportedAt: new Date().toISOString(),
	}

	return JSON.stringify(exportData, null, 2)
}

/**
 * Downloads a JSON string as a file
 */
export const downloadJSONFile = (jsonString: string, fileName: string): void => {
	const blob = new Blob([jsonString], { type: 'application/json' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = fileName
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

/**
 * Copies text to clipboard
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
	try {
		await navigator.clipboard.writeText(text)
		return true
	} catch (err) {
		console.error('Failed to copy to clipboard:', err)
		return false
	}
}

/**
 * Parses and validates imported form JSON
 */
export const parseImportedForm = (
	jsonString: string,
): { success: true; data: ExportedForm } | { success: false; error: string } => {
	try {
		const parsed = JSON.parse(jsonString)

		// Validate required fields
		if (!parsed.name || typeof parsed.name !== 'string') {
			return { success: false, error: 'Invalid form: missing or invalid "name" field' }
		}

		if (!Array.isArray(parsed.components)) {
			return { success: false, error: 'Invalid form: "components" must be an array' }
		}

		// Basic validation of components structure
		for (const component of parsed.components) {
			if (!component.id || !component.name || !component.type) {
				return {
					success: false,
					error: 'Invalid form: components must have id, name, and type fields',
				}
			}
		}

		return {
			success: true,
			data: {
				version: parsed.version || '1.0',
				name: parsed.name,
				components: parsed.components,
				exportedAt: parsed.exportedAt || new Date().toISOString(),
			},
		}
	} catch (err) {
		return {
			success: false,
			error: `Failed to parse JSON: ${err instanceof Error ? err.message : 'Unknown error'}`,
		}
	}
}

/**
 * Determines if JSON should be downloaded or copied based on size
 * Returns true if should download, false if should copy
 */
export const shouldDownloadJSON = (jsonString: string): boolean => {
	// Download if larger than 5000 characters (roughly 5KB)
	return jsonString.length > 5000
}

/**
 * Regenerates all component IDs to avoid conflicts when importing
 */
export const regenerateComponentIds = (components: FormInputComponent[], parentId?: string): FormInputComponent[] => {
	// Map to track old component IDs to new component IDs
	const idMapping = new Map<string, string>()

	// First pass: Regenerate all IDs and build mapping
	const regenerateIds = (components: FormInputComponent[], parentId?: string): FormInputComponent[] => {
		return components.map((component) => {
			const newId = uuidv7()
			idMapping.set(component.id, newId)

			const newComponent: FormInputComponent = {
				...component,
				id: newId,
				containerId: parentId || null,
				children: component.children ? regenerateIds(component.children, newId) : undefined,
			}
			return newComponent
		})
	}

	// Second pass: Update selectedComponentIds in average components
	const updateAverageComponentIds = (components: FormInputComponent[]): FormInputComponent[] => {
		return components.map((component) => {
			if (component.name === 'average' && component.custom) {
				const custom = component.custom as Record<string, unknown>
				if (Array.isArray(custom.selectedComponentIds)) {
					const updatedSelectedIds = custom.selectedComponentIds
						.map((oldId: string) => idMapping.get(oldId) || oldId)
						.filter((id: string) => idMapping.has(id)) // Only keep IDs that exist in the imported form

					return {
						...component,
						custom: {
							...custom,
							selectedComponentIds: updatedSelectedIds,
						},
						children: component.children ? updateAverageComponentIds(component.children) : undefined,
					}
				}
			}

			if (component.children) {
				return {
					...component,
					children: updateAverageComponentIds(component.children),
				}
			}

			return component
		})
	}

	const componentsWithNewIds = regenerateIds(components, parentId)
	return updateAverageComponentIds(componentsWithNewIds)
}

/**
 * Validates and replaces permission template IDs that don't exist in the target organization
 * with the default permission template ID
 */
export const validateAndReplacePermissionTemplates = (
	components: FormInputComponent[],
	availableTemplateIds: string[],
	defaultTemplateId: string,
): FormInputComponent[] => {
	return components.map((component) => {
		// Check if this component's permission template exists in the organization
		const hasValidTemplate = availableTemplateIds.includes(component.permissionTemplateId)

		const updatedComponent: FormInputComponent = {
			...component,
			// Replace with default if not found
			permissionTemplateId: hasValidTemplate ? component.permissionTemplateId : defaultTemplateId,
			// Recursively process children
			children: component.children
				? validateAndReplacePermissionTemplates(component.children, availableTemplateIds, defaultTemplateId)
				: undefined,
		}

		return updatedComponent
	})
}
